import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronDown, FaChevronUp, FaArrowLeft, FaSearch, FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PayrollSchemeSelectEmployeesEntry = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

   function getFinYearId() {
    try {
      const direct = localStorage.getItem("finYearId");
      if (direct) return String(direct);
      const admin = JSON.parse(localStorage.getItem("admin") || "null");
      if (admin?.finYearId) return String(admin.finYearId);
    } catch (_) {}
    return "";
  }

  function tenantHeaders() {
    const fy = getFinYearId();
    return fy ? { "x-fin-year-id": fy } : {};
  }

  function tenantParams(extra = {}) {
    const fy = getFinYearId();
    return fy ? { ...extra, finYearId: fy } : extra;
  }

  const finYearId = useMemo(() => Number(getFinYearId() || 0), []);
  const finYearText = useMemo(() => localStorage.getItem("finYearText") || "", []);

  // Filters
  const [branchId, setBranchId] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");
  const [designationId, setDesignationId] = useState("Select");
  const [schemeId, setSchemeId] = useState("Select");
  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  // Dropdown data
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [schemes, setSchemes] = useState([]);

  // AutoComplete
  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);

  // Grid
  const [records, setRecords] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // UI
  const [showFilters, setShowFilters] = useState(true);
  const [selectAll, setSelectAll] = useState(false);

  // Pagination
  const recordsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const axiosWithFY = useMemo(() => {
    return axios.create({
      baseURL: BASE_URL,
      headers: tenantHeaders(),
      params: tenantParams(),
    });
  }, [BASE_URL, finYearId]);

  const fetchInit = async () => {
    if (!finYearId && !finYearText) {
      toast.error("Financial year missing! Please login again.");
      return;
    }

    try {
      setLoadingInit(true);
      const res = await axiosWithFY.get("/api/payrollEntry/scheme-employees-entry/init");

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load init data");
        return;
      }

      const d = res.data.data || {};
      setDepartments(d.departments || []);
      setBranches(d.branches || []);
      setSchemes(d.schemes || []);
      setDesignations([]); // load on dept change (ASPX)
    } catch (err) {
      console.error("Init error:", err);
      toast.error(err.response?.data?.message || "Failed to load init data");
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      const res = await axiosWithFY.get("/api/payrollEntry/scheme-employees-entry/designations");
      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load designations");
        return;
      }
      setDesignations(res.data.data || []);
    } catch (err) {
      console.error("Designations error:", err);
      toast.error(err.response?.data?.message || "Failed to load designations");
    }
  };

  const fetchList = async (page = 1) => {
    if (!finYearId) {
      toast.error("finYearId missing! Please login again.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/payrollEntry/scheme-employees-entry`, {
        headers: tenantHeaders(),
        params: {
          finYearId,
          page,
          limit: recordsPerPage,
          branchId,
          employeeCode,
          employeeName,
          departmentId,
          designationId,
        },
      });

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = res.data.data || [];
      const mapped = list.map((row, idx) => ({
        id: row.fld_employeeid,
        sNo: (page - 1) * recordsPerPage + (idx + 1),
        employeecode: row.fld_employeecode,
        employee: row.fld_employee,
        departmentname: row.fld_departmentname,
        designationname: row.fld_designationname,
        branchname: row.fld_branchname,
        schemename: row.fld_schemename,
        selected: false,
      }));

      setRecords(mapped);
      setSelectAll(false);

      setCurrentPage(res.data.page || page);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
    } catch (err) {
      console.error("List error:", err);
      toast.error(err.response?.data?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDepartmentChange = async (val) => {
    setDepartmentId(val);
    setDesignationId("Select");
    await fetchDesignations();
  };

  const handleSelectRecord = (id, checked) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, selected: checked } : r)));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setRecords((prev) => prev.map((r) => ({ ...r, selected: checked })));
  };

  const handlePageChange = (page) => {
    if (!page || page === "..." || page === currentPage) return;
    fetchList(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  const fetchCodeSuggestions = async (txt) => {
    const t = String(txt || "").trim();
    if (t.length < 1) {
      setCodeSuggestions([]);
      return;
    }
    try {
      const res = await axiosWithFY.get("/api/payrollEntry/scheme-employees-entry/autocomplete/code", {
        params: tenantParams({ prefix: t }),
      });
      if (res.data?.status) setCodeSuggestions(res.data.data || []);
    } catch {
      // silent
    }
  };

  const fetchNameSuggestions = async (txt) => {
    const t = String(txt || "").trim();
    if (t.length < 1) {
      setNameSuggestions([]);
      return;
    }
    try {
      const res = await axiosWithFY.get("/api/payrollEntry/scheme-employees-entry/autocomplete/name", {
        params: tenantParams({ prefix: t }),
      });
      if (res.data?.status) setNameSuggestions(res.data.data || []);
    } catch {
      // silent
    }
  };

  const handleSubmitEnable = async () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (!selectedIds.length) {
      toast.error("Select any one employee");
      return;
    }

    if (!schemeId || schemeId === "Select") {
      toast.error("Please Select Scheme");
      return;
    }

    const ok = window.confirm("Are you sure to Enable?");
    if (!ok) return;

    try {
      setSaving(true);

      const res = await axiosWithFY.post("/api/payrollEntry/scheme-employees-entry/enable", {
        schemeId,
        employeeIds: selectedIds,
      });

      if (res.data?.status) {
        toast.success(res.data?.message || "Payroll Scheme Enabled Successfully");
        await fetchList(currentPage);
      } else {
        toast.error(res.data?.message || "Payroll Scheme Enabled Failed");
      }
    } catch (err) {
      console.error("Enable error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PAYROLL SCHEME EMPLOYEES ENABLE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Payroll Configration Scheme Employees Enable
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                Total: {totalRecords}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/adminMaster/PayrollSchemeSelectEmployees")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {showFilters && (
              <div className={`p-6 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                {loadingInit ? (
                  <div className={`${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading filters...</div>
                ) : (
                  <div className="grid grid-cols-12 gap-4 items-end">
                    {/* Branch */}
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Branch Name
                      </label>
                      <select
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {branches.map((b) => (
                          <option key={b.fld_Branchid ?? b.id ?? b.value} value={b.fld_branchid ?? b.id ?? b.value}>
                            {b.fld_BranchName ?? b.name ?? b.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Employee Code */}
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Employee Code
                      </label>
                      <input
                        value={employeeCode}
                        onChange={(e) => {
                          setEmployeeCode(e.target.value);
                          fetchCodeSuggestions(e.target.value);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                        placeholder="Type code..."
                        list="empCodeList"
                      />
                      <datalist id="empCodeList">
                        {codeSuggestions.map((s, idx) => (
                          <option key={idx} value={s} />
                        ))}
                      </datalist>
                    </div>

                    {/* Employee Name */}
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Employee Name
                      </label>
                      <input
                        value={employeeName}
                        onChange={(e) => {
                          setEmployeeName(e.target.value);
                          fetchNameSuggestions(e.target.value);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                        placeholder="Type name..."
                        list="empNameList"
                      />
                      <datalist id="empNameList">
                        {nameSuggestions.map((s, idx) => (
                          <option key={idx} value={s} />
                        ))}
                      </datalist>
                    </div>

                    {/* Department */}
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Department
                      </label>
                      <select
                        value={departmentId}
                        onChange={(e) => handleDepartmentChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {departments.map((d) => (
                          <option key={d.fld_deptid} value={d.fld_deptid}>
                            {d.fld_departmentname}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Designation */}
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Designation
                      </label>
                      <select
                        value={designationId}
                        onChange={(e) => setDesignationId(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {designations.map((d) => (
                          <option key={d.fld_desigid} value={d.fld_desigid}>
                            {d.fld_designationname}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Scheme (Required) */}
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Scheme <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={schemeId}
                        onChange={(e) => setSchemeId(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {schemes.map((s) => (
                          <option key={s.fld_schemeid ?? s.id ?? s.value} value={s.fld_schemeid ?? s.id ?? s.value}>
                            {s.fld_schemename ?? s.name ?? s.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Search Button */}
                    <div className="col-span-12 flex justify-end">
                      <button
                        onClick={() => fetchList(1)}
                        disabled={loading}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                      >
                        <FaSearch />
                        Search
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Employee Code</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Employee Name</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Department</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Designation</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Branch</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Scheme</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span>Enable</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan="8" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    records.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.sNo}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.employeecode}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.employee}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.departmentname}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.designationname}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.branchname}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.schemename}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            onChange={(e) => handleSelectRecord(r.id, e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center p-4">
              <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}>
                Total: {totalRecords} | Page: {currentPage} / {totalPages}
              </div>

              <div className="flex gap-1">
                {renderPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => handlePageChange(page)}
                    disabled={page === "..." || loading}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-4">
            <button
              onClick={handleSubmitEnable}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                saving
                  ? "bg-gray-500 text-white cursor-not-allowed"
                  : isDark
                  ? "bg-green-700 hover:bg-green-600 text-white"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              <FaCheck />
              {saving ? "Submitting..." : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default PayrollSchemeSelectEmployeesEntry;
